import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useVelvet } from "@/context/VelvetContext";
import { fmtDate } from "@/lib/calc";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  MessageCircle, Send, Search, Link2, Bot, User as UserIcon,
  FlaskConical, Phone, CircleAlert, RefreshCw, CheckCircle2, BellRing,
} from "lucide-react";
import { toast } from "sonner";
import { pushSupported, pwaInstalled, enablePush, disablePush, pushCurrentlyEnabled } from "@/lib/push";

export default function Inbox() {
  const { profile } = useAuth();
  const { customers } = useVelvet();
  const [status, setStatus] = useState(null);
  const [convs, setConvs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState("");
  const [simOpen, setSimOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const threadScrollRef = useRef(null);
  const isAdmin = ['admin', 'manager'].includes(profile?.role);
  // ---- refresh plumbing (no overlaps, no timer leaks) ----
  const inflightConvs = useRef(false);
  const inflightDetail = useRef(false);
  const selectedIdRef = useRef(null);
  const detailMsgCountRef = useRef(0);
  const draftsRef = useRef({});           // per-conversation typed draft, preserved across refreshes
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  const loadStatus = useCallback(async () => {
    try { setStatus(await apiFetch('/whatsapp/status')); } catch (e) { /* silent */ }
  }, []);

  const loadConvs = useCallback(async ({ silent = false } = {}) => {
    if (inflightConvs.current) return;
    inflightConvs.current = true;
    if (!silent) setLoading(true);
    try {
      const data = await apiFetch('/whatsapp/conversations');
      setConvs(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!silent) toast.error(`No se pudieron cargar las conversaciones: ${e.message}`);
    } finally {
      inflightConvs.current = false;
      if (!silent) setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id, { markRead = true, silent = false } = {}) => {
    if (!id || inflightDetail.current) return;
    inflightDetail.current = true;
    try {
      const data = await apiFetch(`/whatsapp/conversations/${id}`);
      // Only clobber state if user hasn't switched conversations while we waited.
      if (selectedIdRef.current !== id) return;
      setDetail(data);
      if (markRead) {
        apiFetch(`/whatsapp/conversations/${id}/read`, { method: 'POST' })
          .then(() => loadConvs({ silent: true })).catch(() => {});
      }
    } catch (e) {
      if (!silent) toast.error(e.message);
    } finally {
      inflightDetail.current = false;
    }
  }, [loadConvs]);

  // Initial load
  useEffect(() => { loadStatus(); loadConvs(); }, [loadStatus, loadConvs]);

  // Load detail on selection change; preserve typed draft per conversation.
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setBody(draftsRef.current[selectedId] || "");
    loadDetail(selectedId);
    detailMsgCountRef.current = 0;
  }, [selectedId, loadDetail]);

  // Keep draft in sync as user types.
  useEffect(() => {
    if (selectedId) draftsRef.current[selectedId] = body;
  }, [body, selectedId]);

  // ---- Auto-refresh: 5s poll while tab visible + refresh on focus/visibility ----
  useEffect(() => {
    let timer = null;
    const tick = () => {
      if (document.hidden) return;
      loadConvs({ silent: true });
      if (selectedIdRef.current) loadDetail(selectedIdRef.current, { markRead: false, silent: true });
      loadStatus();
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, 5000);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { tick(); start(); }
    };
    const onFocus = () => { if (!document.hidden) tick(); };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [loadConvs, loadDetail, loadStatus]);

  // Smart scroll: only auto-scroll if user was already near the bottom (respect reading position).
  useEffect(() => {
    const n = detail?.messages?.length || 0;
    if (n === detailMsgCountRef.current) return;
    const scroller = threadScrollRef.current;
    const wasAtBottom = !scroller
      || (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight) < 120;
    detailMsgCountRef.current = n;
    if (wasAtBottom) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages?.length]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return convs;
    return convs.filter((c) =>
      (c.display_name || '').toLowerCase().includes(s) ||
      (c.phone_e164 || '').toLowerCase().includes(s) ||
      (c.customer_name || '').toLowerCase().includes(s) ||
      (c.lead_name || '').toLowerCase().includes(s)
    );
  }, [convs, q]);

  const send = async () => {
    if (!body.trim() || !selectedId) return;
    setSending(true);
    try {
      const r = await apiFetch(`/whatsapp/conversations/${selectedId}/messages`, {
        method: 'POST', body: JSON.stringify({ body: body.trim() }),
      });
      // Clear draft only on successful send (send also clears needs_human on the backend).
      draftsRef.current[selectedId] = "";
      setBody("");
      if (r.is_simulated) toast.success('Mensaje enviado (SIMULADO)');
      else toast.success('Mensaje enviado por WhatsApp');
      await loadDetail(selectedId, { markRead: false });
      await loadConvs({ silent: true });
      await loadStatus();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const resolveNeedsHuman = async () => {
    if (!selectedId) return;
    try {
      await apiFetch(`/whatsapp/conversations/${selectedId}/resolve`, { method: 'POST' });
      toast.success('Marcada como atendida');
      await loadDetail(selectedId, { markRead: false });
      await loadConvs({ silent: true });
      await loadStatus();
    } catch (e) { toast.error(e.message); }
  };

  const toggleMode = async (newMode) => {
    if (!selectedId) return;
    try {
      await apiFetch(`/whatsapp/conversations/${selectedId}/mode`, {
        method: 'POST', body: JSON.stringify({ mode: newMode }),
      });
      toast.success(`Modo: ${newMode === 'manual' ? 'Manual' : 'IA'}`);
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const simulate = async ({ from_phone, body, display_name }) => {
    try {
      const r = await apiFetch('/whatsapp/simulate/inbound', {
        method: 'POST',
        body: JSON.stringify({ from_phone, body, display_name }),
      });
      toast.success('Mensaje simulado recibido');
      setSimOpen(false);
      await loadConvs();
      if (r.conversation_id) setSelectedId(r.conversation_id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const linkContact = async ({ customer_id, lead_id, unlink }) => {
    try {
      await apiFetch(`/whatsapp/conversations/${selectedId}/link`, {
        method: 'POST',
        body: JSON.stringify({ customer_id, lead_id, unlink }),
      });
      toast.success(unlink ? 'Contacto desvinculado' : 'Contacto vinculado');
      setLinkOpen(false);
      await loadDetail(selectedId);
      await loadConvs();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-10 max-w-[1500px]" data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="velvet-eyebrow mb-3">CRM</div>
          <h1 className="velvet-page-title flex items-center gap-3">
            Inbox <span className="text-muted-foreground text-lg">·</span>
            <span className="font-serif text-xl text-[hsl(var(--muted-green-dark))]">WhatsApp</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-3">Conversaciones desde tu número de WhatsApp Business.</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip status={status} />
          <NotifButton />
          {isAdmin && (
            <Button variant="outline" className="rounded-sm" onClick={() => setSimOpen(true)} data-testid="simulate-inbound-btn">
              <FlaskConical className="h-4 w-4 mr-1" /> Simular entrante
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 border border-border bg-card" style={{ minHeight: '70vh' }} data-testid="inbox-shell">
        {/* Left rail — conversations */}
        <div className="col-span-12 md:col-span-4 lg:col-span-3 border-r border-border flex flex-col">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
                     className="pl-9 rounded-sm" data-testid="conv-search-input" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="conv-list">
            {loading && <div className="p-6 text-center text-muted-foreground text-sm">Cargando…</div>}
            {!loading && filtered.length === 0 && (
              <div className="p-6 text-center text-muted-foreground text-sm">
                Sin conversaciones aún.<br />
                {isAdmin && 'Usa "Simular entrante" para probar el flujo sin credenciales de Meta.'}
              </div>
            )}
            {filtered.map((c) => (
              <button key={c.id}
                onClick={() => setSelectedId(c.id)}
                data-testid={`conv-item-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${selectedId === c.id ? 'bg-[hsl(var(--soft-blush))]/60' : 'hover:bg-[hsl(var(--muted))]/60'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">
                        {c.customer_name || c.lead_name || c.display_name || c.phone_e164}
                      </span>
                      {c.is_simulated && <SimBadge />}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {c.phone_e164} {c.customer_id ? '· Cliente' : c.lead_id ? `· Lead ${c.lead_stage || ''}` : ''}
                    </div>
                    <div className="text-xs text-foreground/80 truncate mt-1">
                      {c.last_message_direction === 'out' ? '→ ' : ''}
                      {c.last_message_preview || <span className="italic text-muted-foreground">sin mensajes</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end shrink-0 gap-1">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{fmtDate(c.last_message_at)}</span>
                    {c.needs_human && (
                      <span title={c.needs_human_reason || 'Requiere atención humana'}
                            className="bg-[hsl(0_75%_45%)] text-background text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm font-medium"
                            data-testid={`conv-needs-human-${c.id}`}>
                        Atender
                      </span>
                    )}
                    {c.unread_count > 0 && (
                      <span className="bg-[hsl(var(--muted-green-dark))] text-background text-[10px] font-medium min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
                            data-testid={`conv-unread-${c.id}`}>
                        {c.unread_count}
                      </span>
                    )}
                    {c.mode === 'ai' && <span className="text-[9px] uppercase tracking-[0.14em] px-1 border border-border">IA</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right pane — thread */}
        <div className="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              <div className="text-center">
                <MessageCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                Selecciona una conversación
              </div>
            </div>
          ) : (
            <>
              <ThreadHeader detail={detail} onToggleMode={toggleMode} onOpenLink={() => setLinkOpen(true)}
                            onResolve={resolveNeedsHuman} />
              <div className="flex-1 overflow-y-auto p-6 bg-[hsl(60_25%_98%)]" data-testid="message-thread" ref={threadScrollRef}>
                {(detail.messages || []).map((m) => (
                  <MessageBubble key={m.id} m={m} />
                ))}
                <div ref={messagesEndRef} />
              </div>
              <Composer body={body} setBody={setBody} onSend={send} sending={sending}
                        mode={detail.conversation.mode} />
            </>
          )}
        </div>
      </div>

      {isAdmin && <SimulateDialog open={simOpen} onOpenChange={setSimOpen} onSubmit={simulate} />}
      <LinkContactDialog open={linkOpen} onOpenChange={setLinkOpen} customers={customers}
                         current={detail?.conversation} onSubmit={linkContact} />
    </div>
  );
}

function StatusChip({ status }) {
  if (!status) return null;
  const configured = status.configured;
  const enabled = status.enabled;
  const cls = configured && enabled
    ? 'border-[hsl(140_30%_50%)] bg-[hsl(140_40%_92%)] text-foreground'
    : 'border-[hsl(38_60%_65%)] bg-[hsl(48_100%_92%)] text-foreground';
  const Icon = configured && enabled ? CheckCircle2 : CircleAlert;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-1 border ${cls}`}
          title={JSON.stringify(status, null, 2)}
          data-testid="whatsapp-status-chip">
      <Icon className="h-3 w-3" />
      {configured && enabled ? 'Conectado' : 'Modo simulación'}
    </span>
  );
}

function SimBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.14em] px-1 py-0.5 border border-[hsl(38_60%_60%)] bg-[hsl(48_100%_92%)]"
          title="Registro creado por el simulador — no proviene de Meta"
          data-testid="sim-badge">
      <FlaskConical className="h-2.5 w-2.5" /> Sim
    </span>
  );
}

function NotifButton() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();
  const isPWA = pwaInstalled();
  useEffect(() => {
    // Never let a push-init failure bubble into React and crash the Inbox.
    pushCurrentlyEnabled().then(setEnabled).catch((e) => {
      console.warn('[push] pushCurrentlyEnabled failed', e);
      setEnabled(false);
    });
  }, []);
  if (!supported) return null;
  const onClick = async () => {
    setBusy(true);
    try {
      const { apiFetch } = await import('@/lib/supabase');
      if (enabled) { await disablePush(apiFetch); setEnabled(false); toast.success('Notificaciones desactivadas'); }
      else {
        if (!isPWA && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          toast.error('En iPhone: primero "Añadir a pantalla de inicio", luego abre la app desde ese ícono y vuelve a pulsar Activar.');
          return;
        }
        await enablePush(apiFetch); setEnabled(true);
        toast.success('Notificaciones activadas para este dispositivo');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" className="rounded-sm" onClick={onClick} disabled={busy} data-testid="enable-push-btn"
            title={enabled ? 'Notificaciones activas en este dispositivo' : 'Activar notificaciones en este dispositivo'}>
      <BellRing className={"h-4 w-4 mr-1 " + (enabled ? 'text-[hsl(var(--muted-green-dark))]' : '')} />
      {enabled ? 'Notif. activas' : 'Activar notificaciones'}
    </Button>
  );
}

function ThreadHeader({ detail, onToggleMode, onOpenLink, onResolve }) {
  const c = detail.conversation;
  const title = detail.customer?.name || detail.lead?.name || c.display_name || c.phone_e164;
  return (
    <div className="border-b border-border" data-testid="thread-header">
      <div className="px-6 py-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-serif text-2xl truncate">{title}</span>
            {c.is_simulated && <SimBadge />}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
            <Phone className="h-3 w-3" /> {c.phone_e164}
            {detail.customer && <span>· Cliente vinculado</span>}
            {detail.lead && !detail.customer && <span>· Lead: {detail.lead.stage}</span>}
            {!detail.customer && !detail.lead && <span className="italic">· Sin vincular</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ModeToggle mode={c.mode} onChange={onToggleMode} />
          <Button variant="outline" className="rounded-sm" onClick={onOpenLink} data-testid="link-contact-btn">
            <Link2 className="h-4 w-4 mr-1" /> {(detail.customer || detail.lead) ? 'Cambiar vínculo' : 'Vincular contacto'}
          </Button>
        </div>
      </div>
      {c.needs_human && (
        <div className="px-6 py-3 bg-[hsl(0_80%_96%)] border-t border-[hsl(0_75%_80%)] flex items-start justify-between gap-3"
             data-testid="needs-human-banner">
          <div className="text-sm min-w-0">
            <div className="velvet-eyebrow text-[hsl(0_75%_35%)] mb-1">Requiere atención humana</div>
            <div className="text-foreground/90 truncate">
              <span className="font-medium">{c.needs_human_reason || 'ai_handoff'}:</span>{' '}
              {c.needs_human_summary || 'El asistente pasó esta conversación al equipo.'}
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm border-[hsl(0_75%_60%)] shrink-0"
                  onClick={onResolve} data-testid="resolve-needs-human-btn">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Marcar atendida
          </Button>
        </div>
      )}
    </div>
  );
}

function ModeToggle({ mode, onChange }) {
  return (
    <div className="inline-flex border border-border rounded-sm overflow-hidden" data-testid="mode-toggle">
      <button
        type="button"
        onClick={() => mode !== 'manual' && onChange('manual')}
        className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] flex items-center gap-1 ${mode === 'manual' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground hover:text-foreground'}`}
        data-testid="mode-manual-btn">
        <UserIcon className="h-3 w-3" /> Manual
      </button>
      <button
        type="button"
        onClick={() => onChange('ai')}
        className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] flex items-center gap-1 ${mode === 'ai' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground hover:text-foreground'}`}
        title="Activo en el próximo bloque"
        data-testid="mode-ai-btn">
        <Bot className="h-3 w-3" /> IA
      </button>
    </div>
  );
}

function MessageBubble({ m }) {
  const outbound = m.direction === 'out';
  const cls = outbound
    ? 'ml-auto bg-[hsl(140_40%_88%)] border-[hsl(140_30%_60%)]'
    : 'mr-auto bg-white border-border';
  return (
    <div className={`max-w-[70%] mb-3 border ${cls} p-3 rounded-sm`} data-testid={`message-${m.id}`}>
      <div className="text-sm whitespace-pre-wrap">{m.text_body}</div>
      <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        <span>{fmtDate(m.sent_at)}</span>
        {outbound && <span>· {m.status}</span>}
        {m.is_simulated && <SimBadge />}
        {m.status === 'failed' && m.error_message && (
          <span className="text-destructive normal-case">· {m.error_message}</span>
        )}
      </div>
    </div>
  );
}

function Composer({ body, setBody, onSend, sending, mode }) {
  const disabled = mode !== 'manual';
  return (
    <div className="border-t border-border p-3" data-testid="composer">
      {disabled ? (
        <div className="text-xs text-muted-foreground italic px-3 py-4 text-center bg-[hsl(var(--muted))]/40 rounded-sm">
          Esta conversación está en modo IA. Cambia a <b>Manual</b> para responder desde aquí.
        </div>
      ) : (
        <div className="flex gap-2 items-end">
          <Textarea
            value={body} onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSend(); }}
            placeholder="Escribe tu respuesta… (Cmd/Ctrl+Enter para enviar)"
            className="rounded-sm min-h-[60px]"
            data-testid="composer-input" />
          <Button onClick={onSend} disabled={sending || !body.trim()}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-[60px] px-6"
                  data-testid="send-message-btn">
            {sending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}

function SimulateDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ from_phone: '', body: '', display_name: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="simulate-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Simular mensaje entrante</DialogTitle>
          <DialogDescription>
            Genera un mensaje falso para probar el pipeline sin depender de Meta. Se marca <b>Sim</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div><Label>Teléfono (E.164 o dígitos)</Label>
            <Input value={form.from_phone} onChange={set('from_phone')}
                   placeholder="+5215512345678" className="rounded-sm"
                   data-testid="sim-phone" /></div>
          <div><Label>Nombre de perfil (opcional)</Label>
            <Input value={form.display_name} onChange={set('display_name')} className="rounded-sm" /></div>
          <div><Label>Mensaje</Label>
            <Textarea value={form.body} onChange={set('body')} className="rounded-sm min-h-[80px]"
                      data-testid="sim-body" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!form.from_phone.trim() || !form.body.trim()}
                  onClick={() => onSubmit(form)}
                  className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                  data-testid="sim-submit">
            Enviar simulación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkContactDialog({ open, onOpenChange, customers, current, onSubmit }) {
  const [mode, setMode] = useState('customer');
  const [customerId, setCustomerId] = useState('');
  const [leadId, setLeadId] = useState('');
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    if (!open) return;
    setCustomerId(current?.customer_id || '');
    setLeadId(current?.lead_id || '');
    apiFetch('/crm/leads').then(setLeads).catch(() => setLeads([]));
  }, [open, current]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="link-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Vincular contacto</DialogTitle>
          <DialogDescription>Asocia esta conversación a un cliente o lead existente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode('customer')}
                    className={`flex-1 border px-3 py-2 text-sm rounded-sm ${mode === 'customer' ? 'bg-foreground text-background border-foreground' : 'border-border bg-card'}`}
                    data-testid="link-mode-customer">
              Cliente
            </button>
            <button type="button" onClick={() => setMode('lead')}
                    className={`flex-1 border px-3 py-2 text-sm rounded-sm ${mode === 'lead' ? 'bg-foreground text-background border-foreground' : 'border-border bg-card'}`}
                    data-testid="link-mode-lead">
              Lead
            </button>
          </div>
          {mode === 'customer' ? (
            <div>
              <Label>Cliente</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-sm" data-testid="link-customer-select"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>Lead</Label>
              <Select value={leadId} onValueChange={setLeadId}>
                <SelectTrigger className="rounded-sm" data-testid="link-lead-select"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                <SelectContent>
                  {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name} · {l.stage}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-between">
          <Button variant="ghost" className="rounded-sm text-destructive"
                  onClick={() => onSubmit({ unlink: true })}
                  data-testid="link-unlink-btn">
            Desvincular
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={() => onSubmit(mode === 'customer' ? { customer_id: customerId } : { lead_id: leadId })}
                    disabled={mode === 'customer' ? !customerId : !leadId}
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    data-testid="link-confirm-btn">
              Vincular
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
